/** Admin-only rich-text notes about the model/prospect. Autosaved. */
import { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, FloppyDisk } from "@phosphor-icons/react";
import { modelsAdminApi } from "../../../api/models";
import ChapterRichEditor from "../../../components/courses/ChapterRichEditor";

const AUTOSAVE_MS = 1200;

export default function AdminModelNotes() {
  const { t } = useTranslation();
  const { modelId } = useOutletContext();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("idle");
  const [savedAt, setSavedAt] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const note = await modelsAdminApi.getNote(modelId);
        setContent(note.content || { type: "doc", content: [] });
      } finally {
        setLoading(false);
      }
    })();
  }, [modelId]);

  const persist = useCallback(
    async (next) => {
      setStatus("saving");
      try {
        await modelsAdminApi.saveNote(modelId, next);
        setStatus("saved");
        setSavedAt(new Date());
      } catch {
        setStatus("error");
      }
    },
    [modelId],
  );

  const onChange = useCallback(
    (next) => {
      setContent(next);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => persist(next), AUTOSAVE_MS);
    },
    [persist],
  );

  useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);

  if (loading) return <p className="text-sm text-slate-500">{t("common.loading")}</p>;

  return (
    <div data-testid="model-notes-tab" className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">{t("models.notes_help")}</p>
        <span className="text-xs text-slate-500 inline-flex items-center gap-1.5">
          {status === "saving" && (
            <>
              <FloppyDisk size={12} className="animate-pulse" />
              {t("admin.courses.saving")}
            </>
          )}
          {status === "saved" && savedAt && (
            <>
              <CheckCircle size={12} weight="fill" className="text-emerald-500" />
              {t("admin.courses.saved_at", { time: savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) })}
            </>
          )}
        </span>
      </div>
      <ChapterRichEditor value={content} onChange={onChange} placeholder={t("models.notes_placeholder")} />
    </div>
  );
}
